import React, { useMemo, useRef } from "react";

import type { NewsCategoryOption, SelectedCategory } from "../../features/kquant/types";
import { Panel } from "../../components/ui/primitives";

import type {
  FinanceCollectionApi,
  FinanceSectorNewsResponseApi,
  FinanceSectorSentimentResponseApi,
  HotTopicsSectionControlledProps,
  HotTopicsSectionProps,
  SectorPanelData,
} from "./HotTopicsSection.types";
import {
  buildApiUrl,
  clampInt,
  coerceCardToPanelData,
  fetchJson,
  normalizeBasePath,
  normalizeNewsItems,
  pickFirstString,
} from "./HotTopicsSection.utils";
import { HotTopicsCategoryPicker } from "./HotTopicsSection.CategoryPicker";
import { SectorNewsPanel } from "./HotTopicsSection.SectorNewsPanel";

export function HotTopicsSection(props: HotTopicsSectionProps) {
  const isControlled = (props as HotTopicsSectionControlledProps).categories !== undefined;

  const apiBasePath = normalizeBasePath(props.apiBasePath);
  const defaultCollectionsCount = Math.max(1, props.defaultCollectionsCount ?? 3);
  const defaultNewsPerCollection = clampInt(props.defaultNewsPerCollection ?? 3, 1, 9);

  const [internalCategories, setInternalCategories] = React.useState<NewsCategoryOption[]>([]);
  const [internalSelections, setInternalSelections] = React.useState<SelectedCategory[]>([]);
  const [internalPanels, setInternalPanels] = React.useState<SectorPanelData[] | null>(null);

  const categories = isControlled
    ? (props as HotTopicsSectionControlledProps).categories
    : internalCategories;
  const selections = isControlled
    ? (props as HotTopicsSectionControlledProps).selections
    : internalSelections;
  const setSelections = isControlled
    ? (props as HotTopicsSectionControlledProps).setSelections
    : setInternalSelections;

  const inflightRef = useRef<AbortController | null>(null);
  const requestSeqRef = useRef(0);

  function updateSelection(i: number, patch: Partial<SelectedCategory>) {
    setSelections((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      next[i].count = clampInt(Number(next[i].count ?? defaultNewsPerCollection), 1, 9);
      return next;
    });
  }

  function removeSelection(i: number) {
    setSelections((prev) => prev.filter((_, idx) => idx !== i));
  }

  function addSelection() {
    setSelections((prev) => {
      const used = new Set(prev.map((p) => p.key));
      const candidate = categories.find((c) => !used.has(c.key))?.key ?? categories[0]?.key ?? "finance";
      return [...prev, { key: candidate, count: defaultNewsPerCollection }];
    });
  }

  const loadPanels = React.useCallback(
    async (nextSelections: SelectedCategory[], nextCategories: NewsCategoryOption[]) => {
      if (isControlled) return;

      inflightRef.current?.abort();
      const controller = new AbortController();
      inflightRef.current = controller;
      const reqSeq = ++requestSeqRef.current;

      setInternalPanels(null);

      const labelByKey = new Map(nextCategories.map((c) => [c.key, c.label]));

      const results = await Promise.all(
        nextSelections.map(async (sel) => {
          const sectorId = sel.key;
          const count = clampInt(Number(sel.count ?? defaultNewsPerCollection), 1, 9);
          const label = labelByKey.get(sectorId) ?? sectorId;

          const newsUrl = buildApiUrl(
            apiBasePath,
            `/api/v1/finance/sector/news?sector_id=${encodeURIComponent(sectorId)}&limit=${count}`,
          );
          const sentimentUrl = buildApiUrl(
            apiBasePath,
            `/api/v1/finance/sector/sentiment?sector_id=${encodeURIComponent(sectorId)}`,
          );

          const [newsRes, sentimentRes] = await Promise.all([
            fetchJson<FinanceSectorNewsResponseApi>(newsUrl, controller.signal).catch(
              () => ({ data: [] }) as FinanceSectorNewsResponseApi,
            ),
            fetchJson<FinanceSectorSentimentResponseApi>(sentimentUrl, controller.signal).catch(() => null),
          ]);

          const news = normalizeNewsItems(newsRes?.data, count);
          const bullish = sentimentRes?.data?.bullish ?? [];
          const bearish = sentimentRes?.data?.bearish ?? [];

          return {
            sectorId,
            label,
            count,
            news,
            bullish,
            bearish,
          } as SectorPanelData;
        }),
      );

      if (controller.signal.aborted || reqSeq !== requestSeqRef.current) return;
      setInternalPanels(results);
    },
    [apiBasePath, defaultNewsPerCollection, isControlled],
  );

  const externalOnPreview = isControlled
    ? (props as HotTopicsSectionControlledProps).onPreview
    : undefined;

  const handlePreview = React.useCallback(() => {
    if (externalOnPreview) {
      externalOnPreview();
      return;
    }
    void loadPanels(selections, categories);
  }, [externalOnPreview, loadPanels, selections, categories]);

  React.useEffect(() => {
    if (isControlled) return;

    const controller = new AbortController();

    async function bootstrap() {
      setInternalPanels(null);

      try {
        const collectionsUrl = buildApiUrl(apiBasePath, "/api/v1/finance/collections?context=news");
        const collections = await fetchJson<FinanceCollectionApi[]>(collectionsUrl, controller.signal);

        const nextCategories: NewsCategoryOption[] = (collections ?? []).map((c) => ({
          key: c.sector_id,
          label: c.label,
        }));
        setInternalCategories(nextCategories);

        const nextSelections: SelectedCategory[] = nextCategories
          .slice(0, defaultCollectionsCount)
          .map((c) => ({ key: c.key, count: defaultNewsPerCollection }));
        setInternalSelections(nextSelections);

        await loadPanels(nextSelections, nextCategories);
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("[HotTopicsSection] bootstrap failed", err);

        setInternalCategories([]);
        setInternalSelections([]);
        setInternalPanels([]);
      }
    }

    void bootstrap();

    return () => {
      controller.abort();
      inflightRef.current?.abort();
    };
  }, [
    apiBasePath,
    defaultCollectionsCount,
    defaultNewsPerCollection,
    isControlled,
    loadPanels,
  ]);

  const controlledPanels = useMemo(() => {
    if (!isControlled) return null;
    const cprops = props as HotTopicsSectionControlledProps;
    const cards = [
      ...(Array.isArray(cprops.hotTopics) ? cprops.hotTopics : []),
      ...(cprops.preview ? [cprops.preview] : []),
    ];

    if (cprops.hotTopics === null && !cprops.preview) return null;
    if (cards.length === 0) return [];

    const labelByKey = new Map(categories.map((c) => [c.key, c.label]));
    return cards
      .map((card) => {
        const sectorId = pickFirstString((card as any)?.sectorId, (card as any)?.sector_id, (card as any)?.key);
        const fallbackLabel = sectorId ? (labelByKey.get(sectorId) ?? sectorId) : "";
        return coerceCardToPanelData(card, fallbackLabel, defaultNewsPerCollection);
      })
      .filter(Boolean) as SectorPanelData[];
  }, [categories, defaultNewsPerCollection, isControlled, props]);

  const panels = isControlled ? controlledPanels : internalPanels;
  const isLoading = panels === null;

  return (
    <section>
      <Panel className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
          <div className="flex items-baseline gap-4">
            <div className="text-sm font-semibold">Today’s Hot Topics</div>
            <div className="text-xs text-white/55">Automated Daily Selection of Key News</div>
          </div>

          <HotTopicsCategoryPicker
            categories={categories}
            selections={selections}
            updateSelection={updateSelection}
            removeSelection={removeSelection}
            addSelection={addSelection}
            onPreview={handlePreview}
          />
        </div>

        <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
          {isLoading ? (
            Array.from({ length: Math.max(3, selections.length || 0) }).map((_, i) => (
              <SectorNewsPanel key={`loading-${i}`} loading />
            ))
          ) : (
            (panels ?? []).map((p) => <SectorNewsPanel key={p.sectorId} panel={p} />)
          )}
        </div>
      </Panel>
    </section>
  );
}
